import React from 'react';
import { FormattedMessage } from 'react-intl';

import { cssMap } from '@atlaskit/css';
import { Anchor } from '@atlaskit/primitives/compiled/anchor';
import { Box } from '@atlaskit/primitives/compiled/box';
import { Inline } from '@atlaskit/primitives/compiled/inline';
import { Stack } from '@atlaskit/primitives/compiled/stack';
import { Text } from '@atlaskit/primitives/compiled/text';
import { token } from '@atlaskit/tokens';

import { ProfileCardWrapper } from '@confluence/profile/entry-points/ProfileCardWrapper.ts';
import { ProfileLink } from '@confluence/profile/entry-points/ProfileLinkComponent.ts';

import type { AttachmentVersionItem } from '../attachmentsMacroTypes.ts';
import { i18n } from '../attachmentsMacroi18n.ts';
import { buildDownloadUrl } from '../downloadUrl.ts';

import { SmartDate } from './SmartDate.tsx';

type VersionHistoryProps = {
	attachmentId: string;
	versions: AttachmentVersionItem[];
	contentId?: string;
	fileName: string;
	dspBlocksDownloads: boolean;
};

const styles = cssMap({
	headingWrapper: {
		paddingBlockEnd: token('space.0'),
	},
	versionTable: {
		width: '100%',
		borderCollapse: 'collapse',
		borderTopWidth: token('border.width'),
		borderTopStyle: 'solid',
		borderTopColor: token('color.border'),
	},
	versionLink: {
		color: token('color.link'),
		textDecoration: 'none',
	},
	versionRow: {
		borderBottomWidth: token('border.width'),
		borderBottomStyle: 'solid',
		borderBottomColor: token('color.border'),
	},
	versionNumCell: {
		paddingBlockStart: token('space.100'),
		paddingBlockEnd: token('space.100'),
		paddingInlineEnd: token('space.200'),
		whiteSpace: 'nowrap',
		verticalAlign: 'top',
		minWidth: '7rem',
	},
	versionCell: {
		paddingBlockStart: token('space.100'),
		paddingBlockEnd: token('space.100'),
		paddingInlineEnd: token('space.200'),
		verticalAlign: 'top',
	},
	// Rightmost cell has no end padding so the row bar reaches the table's right edge.
	versionDateCell: {
		paddingBlockStart: token('space.100'),
		paddingBlockEnd: token('space.100'),
		paddingInlineEnd: token('space.0'),
		verticalAlign: 'top',
	},
});

export const VersionHistory = ({
	attachmentId,
	versions,
	contentId,
	fileName,
	dspBlocksDownloads,
}: VersionHistoryProps): React.JSX.Element => {
	return (
		<Stack space="space.0">
			<Box xcss={styles.headingWrapper}>
				<Text size="small" color="color.text.subtle" weight="bold">
					<FormattedMessage {...i18n.versionHistory} />
				</Text>
			</Box>
			<Box as="table" xcss={styles.versionTable}>
				<Box as="tbody">
					{versions.map((version) => (
						<Box
							as="tr"
							key={version.version}
							xcss={styles.versionRow}
							testId={`attachment-version-row-${attachmentId}-${version.version}`}
						>
							<Box as="td" xcss={styles.versionNumCell}>
								{dspBlocksDownloads ? (
									<Inline space="space.050" alignBlock="center">
										<Text>
											<FormattedMessage {...i18n.version} /> {version.version}
										</Text>
										{version.isLatestVersion && (
											<Text>
												<FormattedMessage {...i18n.currentVersion} />
											</Text>
										)}
									</Inline>
								) : (
									<Inline space="space.050" alignBlock="center">
										<Anchor
											href={contentId ? buildDownloadUrl(contentId, fileName, version.version) : ''}
											xcss={styles.versionLink}
										>
											<FormattedMessage {...i18n.version} /> {version.version}
										</Anchor>
										{version.isLatestVersion && (
											<Text>
												<FormattedMessage {...i18n.currentVersion} />
											</Text>
										)}
									</Inline>
								)}
							</Box>

							<Box as="td" xcss={styles.versionCell}>
								<Text>
									<FormattedMessage
										{...(version.isNew ? i18n.createdByUser : i18n.modifiedByUser)}
										values={{
											userName: version.modifier.accountId ? (
												<ProfileCardWrapper
													userId={version.modifier.accountId}
													fullName={version.modifier.displayName}
													position="bottom-start"
												>
													<ProfileLink
														userId={version.modifier.accountId}
														fullName={version.modifier.displayName}
														behavior="link"
														referralSource="attachments-macro-version"
														isUnderlinedByDefault={false}
													/>
												</ProfileCardWrapper>
											) : (
												version.modifier.displayName
											),
										}}
									/>
								</Text>
							</Box>

							{version.comment ? (
								<Box as="td" xcss={styles.versionCell}>
									<Text>{version.comment}</Text>
								</Box>
							) : null}

							<Box as="td" xcss={styles.versionDateCell}>
								<Text>
									<SmartDate dateString={version.creationDate} />
								</Text>
							</Box>
						</Box>
					))}
				</Box>
			</Box>
		</Stack>
	);
};
